import { useCallback } from "react";
import { formatTimeWithSetting } from "@produce/shared";
import { useProducerSettings } from "./useProducerSettings";
import { useCrewSettings } from "./useCrewSettings";

/**
 * Renders a "HH:MM(:SS)" time for display.
 *
 * Accepts null/undefined and answers "" for them, exactly like the
 * `formatTimeWithSetting` it wraps. Narrowing this to `string` only pushed a
 * `?? ""` onto a dozen call sites for a case the function already handles.
 */
export type TimeFormatter = (time: string | null | undefined) => string;

/**
 * Renders a time honouring the PRODUCER's 12h/24h schedule setting
 * (`producer_settings.schedule_time_format`) — the schedule creator, the
 * read-only timeline, the lineup, the dashboard card and live tracking.
 *
 * `override` wins when given: the public client-share page has no
 * authenticated producer, so the format travels with the share payload.
 *
 * One hook instead of the same two lines in every screen: the schedule views
 * that each carried their own copy are exactly the ones where a surface was
 * repeatedly forgotten and kept rendering 24h next to 12h.
 *
 * The callback is stable per format, so it is safe to hand to a memoized
 * child or list its identity in a dependency array.
 */
export function useProducerTimeFormatter(override?: "24h" | "12h"): TimeFormatter {
  const { settings } = useProducerSettings();
  const timeFormat = override ?? settings.schedule_time_format;

  return useCallback(
    (time: string | null | undefined) => formatTimeWithSetting(time, timeFormat),
    [timeFormat],
  );
}

/**
 * The crew-side twin: honours the crew member's own setting
 * (`crew_settings.time_format`) on the crew web pages.
 */
export function useCrewTimeFormatter(): TimeFormatter {
  const { settings } = useCrewSettings();
  const timeFormat = settings.time_format;

  return useCallback(
    (time: string | null | undefined) => formatTimeWithSetting(time, timeFormat),
    [timeFormat],
  );
}
